import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { StepProgressService } from '../../../shared/_services/step-progress.service';
import { ThemeService } from '../../../shared/_services/theme.service';

export interface StepDef {
	label: string;
	section: string;
	step: string;
	route: string;
	activeOn: string[];
}

const STEPS_FLOW: StepDef[] = [
	{
		label: 'stepLabelCopertura',
		section: 'steps-verifica',
		step: 'step-uno',
		route: '/steps-verifica/step-uno',
		activeOn: ['/steps-verifica/step-uno'],
	},
	{
		label: 'stepLabelPiano',
		section: 'steps-verifica',
		step: 'step-due',
		route: '/steps-verifica/step-due',
		activeOn: ['/steps-verifica/step-due'],
	},
	{
		label: 'stepLabelAggiunte',
		section: 'steps-preventivo',
		step: 'step-uno',
		route: '/steps-preventivo/step-uno',
		activeOn: ['/steps-preventivo/step-uno'],
	},
	{
		label: 'stepLabelContatti',
		section: 'steps-verifica',
		step: 'step-quattro',
		route: '/steps-verifica/step-tre',
		activeOn: ['/steps-verifica/step-tre', '/steps-verifica/step-quattro'],
	},
	{
		label: 'stepLabelDati',
		section: 'steps-preventivo',
		step: 'step-due',
		route: '/steps-preventivo/step-due',
		activeOn: ['/steps-preventivo/step-due'],
	},
	{
		label: 'stepLabelPagamento',
		section: 'steps-preventivo',
		step: 'step-tre',
		route: '/steps-preventivo/step-tre',
		activeOn: ['/steps-preventivo/step-tre'],
	},
];

@Component({
	selector: 'app-steps-progress-bar',
	standalone: true,
	imports: [CommonModule, TranslatePipe],
	templateUrl: './steps-progress-bar.component.html',
	styles: ``,
})
export class StepsProgressBarComponent implements OnInit, OnDestroy {
	steps: StepDef[] = [];
	activeIndex = -1;
	private routerSub!: Subscription;

	constructor(
		private readonly router: Router,
		private readonly stepService: StepProgressService,
		readonly themeService: ThemeService,
	) {}

	ngOnInit(): void {
		const tenantId = this.themeService.getTenantId();
		this.steps = tenantId === 2
			? STEPS_FLOW
			: STEPS_FLOW.filter((s) => !(s.section === 'steps-preventivo' && s.step === 'step-uno'));
		this.updateActive(this.router.url);
		this.routerSub = this.router.events
			.pipe(filter((e) => e instanceof NavigationEnd))
			.subscribe((e: any) => this.updateActive(e.urlAfterRedirects));
	}

	private updateActive(url: string): void {
		const clean = url.split('?')[0];
		this.activeIndex = this.steps.findIndex((s) => s.activeOn.some((a) => clean.startsWith(a)));
	}

	isCompleted(index: number): boolean {
		const s = this.steps[index];
		return this.stepService.isStepCompleted(s.section, s.step);
	}

	isCurrent(index: number): boolean {
		return index === this.activeIndex;
	}

	private isLocked(index: number): boolean {
		const s = this.steps[index];
		return s.section === 'steps-verifica' && (s.step === 'step-tre' || s.step === 'step-quattro');
	}

	canNavigate(index: number): boolean {
		return this.isCompleted(index) && !this.isCurrent(index) && !this.isLocked(index);
	}

	navigate(index: number): void {
		if (this.canNavigate(index)) {
			this.router.navigateByUrl(this.steps[index].route);
		}
	}

	get currentLabel(): string {
		return this.activeIndex >= 0 ? this.steps[this.activeIndex].label : '';
	}

	get progressPercent(): number {
		if (this.activeIndex < 0) return 0;
		return Math.round(((this.activeIndex + 1) / this.steps.length) * 100);
	}

	ngOnDestroy(): void {
		this.routerSub?.unsubscribe();
	}
}
